/**
 * Cohort dropdown backed by the tenant's catalog (FR-ROSTER-02, AC-ROSTER-002).
 *
 * Rules the control enforces so the roster stays consistent:
 *   - options come from `GET /cohort-values/v1`, grouped by category and ordered
 *     by the tenant's `sort_order`; the LABEL is displayed, the CODE is stored;
 *   - deprecated values are filtered out of new assignments, but the value a
 *     player already carries stays selectable so editing their jersey number
 *     does not silently re-cohort them (AC-ROSTER-006);
 *   - Team Admin gets an "Add cohort value" affordance; the owning form hosts
 *     `AddCohortValueModal` (a dialog with its own <form> must not be nested
 *     inside the player form, or its submit would bubble into it) and selects
 *     the new code on return.
 *
 * There is no birth-date or age input anywhere near this control: cohort IS the
 * seniority mechanism (AC-ROSTER-003 / AC-ROSTER-004).
 */
import { useId, useMemo } from 'react';

import type { CohortValue } from '../../lib/api-types';

import { COHORT_CATEGORY_LABELS } from './add-cohort-value-modal';

export interface CohortSelectProps {
  /** Selected cohort code, or `''` when nothing is chosen yet. */
  value: string;
  onChange: (code: string) => void;
  /** The whole catalog, deprecated rows included. */
  values: readonly CohortValue[];
  /** Inline validation message from the form or the server. */
  error?: string | null;
  /** Team Admin only — cohort-catalog writes are `team_admin` on the gateway. */
  canAddValue?: boolean;
  /** Asks the owning form to open the "Add cohort value" dialog. */
  onAddValueRequested?: () => void;
  isDisabled?: boolean;
}

interface CohortGroup {
  category: string;
  options: readonly CohortValue[];
}

/** Assignable options, plus the (possibly deprecated) code already selected. */
function selectableValues(
  values: readonly CohortValue[],
  selectedCode: string
): readonly CohortValue[] {
  return values.filter(
    (value) => !value.is_deprecated || value.code.toLowerCase() === selectedCode.toLowerCase()
  );
}

function groupByCategory(values: readonly CohortValue[]): readonly CohortGroup[] {
  const groups = new Map<string, CohortValue[]>();
  for (const value of values) {
    const bucket = groups.get(value.category);
    if (bucket === undefined) groups.set(value.category, [value]);
    else bucket.push(value);
  }
  return Array.from(groups, ([category, options]) => ({ category, options }));
}

function categoryLabel(category: string): string {
  return COHORT_CATEGORY_LABELS[category as keyof typeof COHORT_CATEGORY_LABELS] ?? category;
}

export function CohortSelect({
  value,
  onChange,
  values,
  error = null,
  canAddValue = false,
  onAddValueRequested,
  isDisabled = false,
}: CohortSelectProps): JSX.Element {
  const selectId = `cohort-${useId()}`;
  const errorId = `${selectId}-error`;
  const hintId = `${selectId}-hint`;

  const options = useMemo(() => selectableValues(values, value), [value, values]);
  const groups = useMemo(() => groupByCategory(options), [options]);
  const selected = options.find((option) => option.code === value) ?? null;
  const isSelectedDeprecated = selected?.is_deprecated === true;

  return (
    <div className={`field${error !== null ? ' field--invalid' : ''}`}>
      <label className="field__label" htmlFor={selectId}>
        Cohort
        <span aria-hidden="true"> *</span>
        <span className="visually-hidden"> (required)</span>
      </label>

      <div className="cohort-select__row">
        <select
          id={selectId}
          className="field__control"
          value={value}
          disabled={isDisabled}
          aria-invalid={error !== null ? true : undefined}
          aria-describedby={`${hintId}${error !== null ? ` ${errorId}` : ''}`}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Select a cohort…</option>
          {groups.map((group) => (
            <optgroup key={group.category} label={categoryLabel(group.category)}>
              {group.options.map((option) => (
                <option key={option.id} value={option.code}>
                  {option.label} ({option.code}){option.is_deprecated ? ' — retired' : ''}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        {canAddValue ? (
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onAddValueRequested}
            disabled={isDisabled}
          >
            Add cohort value
          </button>
        ) : null}
      </div>

      <span className="field__hint" id={hintId}>
        Grade, class year or squad. Cohort replaces date of birth on this platform and is required
        for every player.
      </span>

      {isSelectedDeprecated ? (
        <span className="field__hint">
          This cohort is retired. It stays on players who already have it, but pick another value
          for new assignments.
        </span>
      ) : null}

      {options.length === 0 ? (
        <span className="field__hint">
          {canAddValue
            ? 'Your cohort catalog is empty. Add a value to start building the roster.'
            : 'No cohort values are available yet. Ask a Team Admin to add one.'}
        </span>
      ) : null}

      {error !== null ? (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
